// src/modules/communications/pages/AllCommunicationsPage.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import {
  FaPhone,
  FaSms,
  FaUser,
  FaFilter,
  FaSpinner,
  FaChevronDown,
  FaChevronUp,
  FaPhoneVolume,
  FaPhoneSlash,
  FaSearch,
  FaTimes,
  FaCircle,
  FaArrowDown,
  FaArrowUp,
  FaPlus,
  FaReply,
  FaChevronLeft,
  FaChevronRight,
  FaVolumeUp
} from 'react-icons/fa';
import { getAllCommunications } from '../services/communicationService';
import DashboardLayout from '../../../layouts/DashboardLayout';
import {
  formatDateTime,
  formatPhoneNumber,
  formatDuration,
  getStatusColor
} from '../utils/formatUtils';
import { useCommunication } from '../context/CommunicationContext';
import { useNavigate } from 'react-router-dom';
import TranscriptionSection from '../../../components/communications/TranscriptionSection';

// Styled Components
const PageContainer = styled.div`
  padding: ${props => props.theme.spacing(2)};
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing(3)};
`;

const Title = styled.h1`
  color: ${props => props.theme.colors.primary};
  margin: 0;
`;

const FiltersContainer = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(2)};
  margin-bottom: ${props => props.theme.spacing(3)};
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing(2)};
  align-items: center;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 150px;
  
  label {
    font-size: ${props => props.theme.typography.fontSize.small};
    color: ${props => props.theme.colors.textSecondary};
    margin-bottom: ${props => props.theme.spacing(0.5)};
  }
  
  select {
    padding: ${props => props.theme.spacing(1)};
    border-radius: ${props => props.theme.borderRadius};
    background: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.textPrimary};
    border: 1px solid ${props => props.theme.colors.inputGray};
    font-size: ${props => props.theme.typography.fontSize.small};
    
    &:focus {
      outline: none;
      border-color: ${props => props.theme.colors.primary};
    }
  }
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.inputGray};
  border-radius: ${props => props.theme.borderRadius};
  padding: 0 ${props => props.theme.spacing(1)};
  flex-grow: 1;
  max-width: 300px;
  
  input {
    flex-grow: 1;
    padding: ${props => props.theme.spacing(1)};
    background: transparent;
    border: none;
    color: ${props => props.theme.colors.textPrimary};
    font-size: ${props => props.theme.typography.fontSize.small};
    
    &:focus {
      outline: none;
    }
  }
  
  svg {
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const ClearFiltersButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.primary};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(0.5)};
  font-size: ${props => props.theme.typography.fontSize.small};
  
  &:hover {
    text-decoration: underline;
  }
`;

const SummaryBox = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(2)};
  margin-bottom: ${props => props.theme.spacing(3)};
  font-size: ${props => props.theme.typography.fontSize.small};
  
  .summary-count {
    font-weight: ${props => props.theme.typography.fontWeight.medium};
    color: ${props => props.theme.colors.primary};
  }
`;

const CommList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing(2)};
`;

const CommItem = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const CommHeader = styled.div`
  display: flex;
  align-items: center;
  padding: ${props => props.theme.spacing(2)};
  cursor: pointer;
  
  &:hover {
    background: ${props => props.theme.colors.background};
  }
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const CommIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${props => {
    if (props.type === 'call') {
      return props.direction === 'inbound' ? '#27ae60' : '#3498db';
    } else {
      return props.direction === 'inbound' ? '#e67e22' : '#9b59b6';
    }
  }};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${props => props.theme.spacing(2)};
  flex-shrink: 0;
`;

const CommInfo = styled.div`
  flex-grow: 1;
  margin-right: ${props => props.theme.spacing(2)};
  
  .comm-type {
    font-weight: ${props => props.theme.typography.fontWeight.medium};
    display: flex;
    align-items: center;
    gap: ${props => props.theme.spacing(1)};
  }
  
  .comm-numbers {
    font-size: ${props => props.theme.typography.fontSize.small};
    color: ${props => props.theme.colors.textSecondary};
    margin-top: ${props => props.theme.spacing(0.5)};
  }
  
  @media (max-width: 768px) {
    flex-basis: 100%;
    margin-bottom: ${props => props.theme.spacing(1)};
  }
`;

const CommTime = styled.div`
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${props => props.theme.typography.fontSize.small};
  margin-right: ${props => props.theme.spacing(2)};
  white-space: nowrap;
  
  @media (max-width: 768px) {
    margin-left: ${props => props.theme.spacing(5)};
  }
`;

const CommStatus = styled.div`
  display: inline-block;
  padding: ${props => props.theme.spacing(0.5)} ${props => props.theme.spacing(1)};
  border-radius: 10px;
  font-size: ${props => props.theme.typography.fontSize.small};
  background-color: ${props => getStatusColor(props.status)}22;
  color: ${props => getStatusColor(props.status)};
  margin-right: ${props => props.theme.spacing(2)};
  white-space: nowrap;
  
  display: flex;
  align-items: center;
  gap: 4px;
  
  svg {
    font-size: 8px;
  }
  
  @media (max-width: 768px) {
    margin-left: ${props => props.theme.spacing(5)};
  }
`;

const CommLead = styled.div`
  margin-right: ${props => props.theme.spacing(2)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
  
  a {
    display: inline-flex;
    align-items: center;
    gap: ${props => props.theme.spacing(0.5)};
    color: ${props => props.theme.colors.primary};
    font-size: ${props => props.theme.typography.fontSize.small};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  .no-lead {
    font-style: italic;
    color: ${props => props.theme.colors.textSecondary};
    font-size: ${props => props.theme.typography.fontSize.small};
  }
  
  .placeholder-badge {
    font-size: 0.7rem;
    padding: 2px 6px;
    border-radius: 10px;
    background: ${props => props.theme.colors.warning}33;
    color: ${props => props.theme.colors.warning};
  }
  
  @media (max-width: 768px) {
    margin-left: ${props => props.theme.spacing(5)};
    margin-bottom: ${props => props.theme.spacing(1)};
  }
`;

const ExpandIcon = styled.div`
  flex-shrink: 0;
  
  svg {
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const CommDetails = styled.div`
  padding: ${props => props.theme.spacing(2)};
  padding-top: 0;
  border-top: 1px solid ${props => props.theme.colors.inputGray}22;
  background: ${props => props.theme.colors.background};
`;

const DetailRow = styled.div`
  margin-bottom: ${props => props.theme.spacing(2)};
  
  label {
    display: block;
    font-size: ${props => props.theme.typography.fontSize.small};
    color: ${props => props.theme.colors.textSecondary};
    margin-bottom: ${props => props.theme.spacing(0.5)};
  }
`;

const SmsBody = styled.div`
  background: ${props => props.theme.colors.surface};
  padding: ${props => props.theme.spacing(2)};
  border-radius: ${props => props.theme.borderRadius};
  margin-top: ${props => props.theme.spacing(1)};
  white-space: pre-wrap;
  font-size: ${props => props.theme.typography.fontSize.small};
`;

const MediaGallery = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing(1)};
  margin-top: ${props => props.theme.spacing(1)};
  
  a {
    width: 100px;
    height: 100px;
    overflow: hidden;
    border-radius: ${props => props.theme.borderRadius};
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
`;

const AudioPlayerRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  background: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(1.5)};
  border-left: 3px solid ${props => props.theme.colors.primary};
  width: 100%;

  .audio-icon {
    color: ${props => props.theme.colors.primary};
    font-size: 1.1rem;
    flex-shrink: 0;
  }

  audio {
    flex: 1;
    min-width: 0;
    height: 36px;
  }
`;

const isAudioUrl = (url) => {
  const ext = url.split('.').pop().split('?')[0].toLowerCase();
  return ['amr', 'ogg', 'mp3', 'wav', 'm4a', '3gp', 'aac', 'mp4'].includes(ext);
};

const ActionButtons = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing(2)};
  margin-top: ${props => props.theme.spacing(2)};
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  padding: ${props => props.theme.spacing(1)} ${props => props.theme.spacing(2)};
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.inputGray};
  border-radius: ${props => props.theme.borderRadius};
  color: ${props => props.theme.colors.textPrimary};
  font-size: ${props => props.theme.typography.fontSize.small};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.colors.primary}22;
    border-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.primary};
  }
  
  svg {
    font-size: 1rem;
  }
  
  @media (max-width: 768px) {
    flex: 1;
    justify-content: center;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  
  svg {
    font-size: 2rem;
    color: ${props => props.theme.colors.primary};
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing(6)};
  text-align: center;
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  
  svg {
    font-size: 3rem;
    color: ${props => props.theme.colors.inputGray};
    margin-bottom: ${props => props.theme.spacing(2)};
  }
  
  p {
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const PaginationContainer = styled.div`
  margin-top: ${props => props.theme.spacing(3)};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing(2)};
`;

const PaginationInfo = styled.div`
  font-size: ${props => props.theme.typography.fontSize.small};
  color: ${props => props.theme.colors.textSecondary};
`;

const PaginationNav = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const PageButton = styled.button`
  min-width: 36px;
  height: 36px;
  padding: 0 ${props => props.theme.spacing(1)};
  border: 1px solid ${props => props.active ? props.theme.colors.primary : props.theme.colors.inputGray};
  border-radius: ${props => props.theme.borderRadius};
  background: ${props => props.active ? props.theme.colors.primary : props.theme.colors.surface};
  color: ${props => props.active ? '#fff' : props.theme.colors.textPrimary};
  font-size: ${props => props.theme.typography.fontSize.small};
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  opacity: ${props => props.disabled ? 0.4 : 1};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: ${props => props.active ? props.theme.colors.primary : props.theme.colors.primary + '22'};
    border-color: ${props => props.theme.colors.primary};
    color: ${props => props.active ? '#fff' : props.theme.colors.primary};
  }
`;

const PageEllipsis = styled.span`
  min-width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${props => props.theme.typography.fontSize.small};
`;

const PerPageSelector = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  font-size: ${props => props.theme.typography.fontSize.small};
  color: ${props => props.theme.colors.textSecondary};

  select {
    padding: ${props => props.theme.spacing(0.5)} ${props => props.theme.spacing(1)};
    border-radius: ${props => props.theme.borderRadius};
    background: ${props => props.theme.colors.surface};
    color: ${props => props.theme.colors.textPrimary};
    border: 1px solid ${props => props.theme.colors.inputGray};
    font-size: ${props => props.theme.typography.fontSize.small};
    cursor: pointer;

    &:focus {
      outline: none;
      border-color: ${props => props.theme.colors.primary};
    }
  }
`;

/**
 * All Communications Page - displays all communications with filtering
 * @returns {React.ReactElement} All Communications Page component
 */
const AllCommunicationsPage = () => {
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [filters, setFilters] = useState({
    type: '',
    direction: '',
    hasLead: '',
    page: 1,
    limit: 12
  });
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0
  });
  const [searchInput, setSearchInput] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const debounceRef = useRef(null);

  const { openCallModal, openSmsComposer } = useCommunication();
  const navigate = useNavigate();

  const URL_API = process.env.REACT_APP_API_PUBLIC || "https://api.mrpatchasphalt.com";

  // Debounce search input - wait 500ms after typing stops
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(searchInput);
      setFilters(prev => ({ ...prev, page: 1 }));
    }, 500);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  // Fetch communications when filters or debounced query change
  useEffect(() => {
    const fetchCommunications = async () => {
      setLoading(true);
      try {
        const queryParams = { ...filters };
        if (debouncedQuery) {
          queryParams.query = debouncedQuery;
        }

        const data = await getAllCommunications(queryParams);
        setCommunications(data.communications || []);
        setSummary(data.summary);
        setPagination({
          currentPage: data.currentPage,
          totalPages: data.totalPages,
          totalItems: data.totalItems
        });
        setError(null);
      } catch (err) {
        console.error('Error fetching communications:', err);
        setError('Failed to load communications');
      } finally {
        setLoading(false);
      }
    };

    fetchCommunications();
  }, [filters, debouncedQuery]);

  // Handle filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
      page: 1
    }));
  };

  // Handle page change
  const handlePageChange = (page) => {
    if (page < 1 || page > pagination.totalPages) return;
    setFilters(prev => ({ ...prev, page }));
    window.scrollTo(0, 0);
  };

  // Handle per-page change
  const handlePerPageChange = (e) => {
    setFilters(prev => ({
      ...prev,
      limit: parseInt(e.target.value),
      page: 1
    }));
  };

  // Build page numbers with ellipsis
  const getPageNumbers = useCallback(() => {
    const { currentPage, totalPages } = pagination;
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages = [];
    pages.push(1);
    if (currentPage > 3) pages.push('...');
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (currentPage < totalPages - 2) pages.push('...');
    pages.push(totalPages);
    return pages;
  }, [pagination]);

  // Toggle item expansion
  const toggleItemExpansion = (id) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Handle action buttons
  const handleCall = (phoneNumber, leadId) => {
    openCallModal(phoneNumber, leadId);
  };

  const handleSms = (phoneNumber, leadId) => {
    openSmsComposer(phoneNumber, leadId);
  };

  const handleCreateLead = (communication) => {
    navigate('/leads/new', {
      state: {
        phone: communication.from_number,
        name: `New Lead (${formatPhoneNumber(communication.from_number)})`
      }
    });
  };

  const handleUpgradeLead = (leadId) => {
    navigate(`/communications/placeholder-leads/${leadId}`);
  };

  // Handle lead click
  const handleLeadClick = (e, leadId) => {
    e.stopPropagation();
    navigate(`/leads/view/${leadId}`);
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      type: '',
      direction: '',
      hasLead: '',
      page: 1,
      limit: 12
    });
    setSearchInput('');
    setDebouncedQuery('');
  };

  // Clear search
  const clearSearch = () => {
    setSearchInput('');
    setDebouncedQuery('');
  };
  
  return (
    <DashboardLayout>
      <PageContainer>
        <PageHeader>
          <Title>All Communications</Title>
        </PageHeader>
        
        {/* Filters */}
        <FiltersContainer>
          <FilterGroup>
            <label>Type</label>
            <select 
              name="type" 
              value={filters.type} 
              onChange={handleFilterChange}
            >
              <option value="">All Types</option>
              <option value="call">Calls Only</option>
              <option value="sms">SMS Only</option>
            </select>
          </FilterGroup>
          
          <FilterGroup>
            <label>Direction</label>
            <select 
              name="direction" 
              value={filters.direction} 
              onChange={handleFilterChange}
            >
              <option value="">All Directions</option>
              <option value="inbound">Inbound Only</option>
              <option value="outbound">Outbound Only</option>
            </select>
          </FilterGroup>
          
          <FilterGroup>
            <label>Lead Status</label>
            <select 
              name="hasLead" 
              value={filters.hasLead} 
              onChange={handleFilterChange}
            >
              <option value="">All</option>
              <option value="true">With Lead</option>
              <option value="false">Without Lead</option>
            </select>
          </FilterGroup>
          
          <SearchBox>
            <FaSearch />
            <input
              type="text"
              placeholder="Search phone, name, or content"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            {searchInput && (
              <FaTimes
                style={{ cursor: 'pointer' }}
                onClick={clearSearch}
              />
            )}
          </SearchBox>
          
          <ClearFiltersButton onClick={clearFilters}>
            <FaTimes /> Clear Filters
          </ClearFiltersButton>
        </FiltersContainer>
        
        {/* Summary Box */}
        {summary && (
          <SummaryBox>
            Showing <span className="summary-count">{communications.length}</span> of{' '}
            <span className="summary-count">{pagination.totalItems}</span> communications
            {filters.type && ` • Type: ${filters.type}`}
            {filters.direction && ` • Direction: ${filters.direction}`}
            {filters.hasLead && ` • ${filters.hasLead === 'true' ? 'With Lead' : 'Without Lead'}`}
            {debouncedQuery && ` • Search: "${debouncedQuery}"`}
          </SummaryBox>
        )}
        
        {/* Communications List */}
        {loading && communications.length === 0 ? (
          <LoadingContainer>
            <FaSpinner />
          </LoadingContainer>
        ) : communications.length === 0 ? (
          <EmptyState>
            <FaFilter />
            <p>No communications found matching your filters.</p>
          </EmptyState>
        ) : (
          <CommList>
            {communications.map((comm) => {
              const isExpanded = expandedItems.has(`${comm.type}-${comm.id}`);
              return (
                <CommItem key={`${comm.type}-${comm.id}`}>
                  <CommHeader onClick={() => toggleItemExpansion(`${comm.type}-${comm.id}`)}>
                    <CommIcon type={comm.type} direction={comm.direction}>
                      {comm.type === 'call' ? (
                        comm.direction === 'inbound' ? <FaPhoneVolume /> : <FaPhoneSlash />
                      ) : (
                        <FaSms />
                      )}
                    </CommIcon>
                    
                    <CommInfo>
                      <div className="comm-type">
                        {comm.direction === 'inbound' ? 'Incoming' : 'Outgoing'} {comm.type === 'call' ? 'Call' : 'SMS'}
                      </div>
                      <div className="comm-numbers">
                        {formatPhoneNumber(comm.from_number)} → {formatPhoneNumber(comm.to_number)}
                      </div>
                    </CommInfo>
                    
                    <CommTime>
                      {formatDateTime(comm.timestamp)}
                    </CommTime>
                    
                    <CommStatus status={comm.status}>
                      <FaCircle /> {comm.status}
                    </CommStatus>
                    
                    <CommLead>
                      {comm.lead ? (
                        <a 
                          href={`/leads/view/${comm.lead.id}`}
                          onClick={(e) => handleLeadClick(e, comm.lead.id)}
                        >
                          <FaUser />
                          {comm.lead.name}
                          {comm.lead.isPlaceholder && (
                            <span className="placeholder-badge">Unknown</span>
                          )}
                        </a>
                      ) : (
                        <span className="no-lead">No lead</span>
                      )}
                    </CommLead>
                    
                    <ExpandIcon>
                      {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                    </ExpandIcon>
                  </CommHeader>
                  
                  {isExpanded && (
                    <CommDetails>
                      {comm.type === 'call' ? (
                        <>
                          <DetailRow>
                            <label>Direction:</label>
                            <div>
                              {comm.direction === 'inbound' ? (
                                <><FaArrowDown /> Incoming from {formatPhoneNumber(comm.from_number)}</>
                              ) : (
                                <><FaArrowUp /> Outgoing to {formatPhoneNumber(comm.to_number)}</>
                              )}
                            </div>
                          </DetailRow>
                          
                          <DetailRow>
                            <label>Duration:</label>
                            <div>{formatDuration(comm.duration)}</div>
                          </DetailRow>
                          
                          {comm.recording_path && (
                            <DetailRow>
                              <label>Recording:</label>
                              <audio controls src={`${URL_API}${comm.recording_path}`}>
                                Your browser does not support the audio element.
                              </audio>
                            </DetailRow>
                          )}
                          
                          {comm.notes && (
                            <DetailRow>
                              <label>Notes:</label>
                              <div>{comm.notes}</div>
                            </DetailRow>
                          )}
                          {comm.recording_path && (
                            <TranscriptionSection
                              callId={comm.id}
                              transcription={comm.transcription}
                              transcriptionStatus={comm.transcription_status}
                              transcribedAt={comm.transcribed_at}
                            />
                          )}
                        </>
                      ) : (
                        <>
                          <DetailRow>
                            <label>Message:</label>
                            <SmsBody>{comm.body}</SmsBody>
                          </DetailRow>
                          
                          {comm.media_paths && comm.media_paths.length > 0 && (
                            <DetailRow>
                              <label>Media:</label>
                              <MediaGallery>
                                {comm.media_paths.map((url, idx) => (
                                  isAudioUrl(url) ? (
                                    <AudioPlayerRow key={idx}>
                                      <FaVolumeUp className="audio-icon" />
                                      <audio controls preload="metadata">
                                        <source src={`${URL_API}${url}`} />
                                      </audio>
                                    </AudioPlayerRow>
                                  ) : (
                                    <a
                                      key={idx}
                                      href={`${URL_API}${url}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <img src={`${URL_API}${url}`} alt={`Media ${idx + 1}`} width="100%" />
                                    </a>
                                  )
                                ))}
                              </MediaGallery>
                            </DetailRow>
                          )}
                        </>
                      )}
                      
                      <ActionButtons>
                        {!comm.lead && (
                          <ActionButton onClick={() => handleCreateLead(comm)}>
                            <FaPlus /> Create Lead
                          </ActionButton>
                        )}
                        
                        {comm.lead?.isPlaceholder && (
                          <ActionButton onClick={() => handleUpgradeLead(comm.lead.id)}>
                            <FaUser /> Upgrade to Customer
                          </ActionButton>
                        )}
                        
                        <ActionButton onClick={() => handleCall(
                          comm.direction === 'inbound' ? comm.from_number : comm.to_number,
                          comm.lead?.id
                        )}>
                          <FaPhone /> Call
                        </ActionButton>
                        
                        <ActionButton onClick={() => handleSms(
                          comm.direction === 'inbound' ? comm.from_number : comm.to_number,
                          comm.lead?.id
                        )}>
                          {comm.type === 'sms' ? <FaReply /> : <FaSms />}
                          {comm.type === 'sms' ? 'Reply' : 'Send SMS'}
                        </ActionButton>
                      </ActionButtons>
                    </CommDetails>
                  )}
                </CommItem>
              );
            })}
          </CommList>
        )}
        
        {/* Pagination */}
        {communications.length > 0 && (
          <PaginationContainer>
            <PaginationInfo>
              Showing {((pagination.currentPage - 1) * filters.limit) + 1}–{Math.min(pagination.currentPage * filters.limit, pagination.totalItems)} of {pagination.totalItems}
            </PaginationInfo>

            {pagination.totalPages > 1 && (
              <PaginationNav>
                <PageButton
                  disabled={pagination.currentPage === 1}
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                >
                  <FaChevronLeft size={12} />
                </PageButton>
                {getPageNumbers().map((p, idx) =>
                  p === '...' ? (
                    <PageEllipsis key={`ellipsis-${idx}`}>...</PageEllipsis>
                  ) : (
                    <PageButton
                      key={p}
                      active={p === pagination.currentPage}
                      onClick={() => handlePageChange(p)}
                    >
                      {p}
                    </PageButton>
                  )
                )}
                <PageButton
                  disabled={pagination.currentPage === pagination.totalPages}
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                >
                  <FaChevronRight size={12} />
                </PageButton>
              </PaginationNav>
            )}

            <PerPageSelector>
              <span>Show</span>
              <select value={filters.limit} onChange={handlePerPageChange}>
                <option value={12}>12</option>
                <option value={24}>24</option>
                <option value={36}>36</option>
                <option value={48}>48</option>
                <option value={100}>100</option>
              </select>
              <span>per page</span>
            </PerPageSelector>
          </PaginationContainer>
        )}
      </PageContainer>
    </DashboardLayout>
  );
};

export default AllCommunicationsPage;